import { useAppDispatch, useTypedSelector } from "@/config/configureAppStore";
import { setStudioVoiceEnabled } from "@/reducers/slices/clientProjectPopupSlice/clientProjectPopupSlice";
import { ExpandMore } from "@mui/icons-material";
import {
  Accordion,
  accordionClasses,
  AccordionDetails,
  AccordionSummary,
  Checkbox,
  FormControlLabel,
  Typography
} from "@mui/material";
import { ChangeEvent, useMemo } from "react";

// Keep it open by default
// Currently the only feature that is available is `disable cloning` based on useStudioVoices
const AdvancedFeatures = () => {
  const workspaces = useTypedSelector((state) => state.user.linkedWorkspaces);
  const activeWorkspaceId = useTypedSelector(
    (state) => state.user.activeWorkspaceId
  );

  const currentWorkspace = useMemo(() => {
    return workspaces.find(
      (workspace) => workspace.workspaceId === activeWorkspaceId
    );
  }, [activeWorkspaceId, workspaces]);

  const useStudioVoices = useMemo(() => {
    return Boolean(currentWorkspace?.useStudioVoice);
  }, [currentWorkspace]);

  //   Will have other features in the future. So keeping it as a separate variable.
  const showAdvancedFeatures = useMemo(() => {
    if (!useStudioVoices) return false;
    return true;
  }, [useStudioVoices]);

  const { studioVoiceEnabled } = useTypedSelector(
    (state) => state.clientProjectPopup
  );
  const dispatch = useAppDispatch();
  const handleStudioVoiceEnabled = (e: ChangeEvent<HTMLInputElement>) => {
    dispatch(setStudioVoiceEnabled(e.target.checked));
  };

  if (!showAdvancedFeatures) return null;

  return (
    <Accordion
      square={true}
      sx={{
        p: 0,
        [`&.${accordionClasses.root}`]: {
          backgroundColor: "inherit"
        },
        [`&.${accordionClasses.root}::before`]: {
          display: "none"
        },
        "&.Mui-disabled": {
          pointerEvents: "none",
          opacity: 0.5
        }
      }}
      elevation={0}
      disableGutters
      defaultExpanded
    >
      <AccordionSummary
        expandIcon={<ExpandMore sx={{ color: "action.active" }} />}
        aria-controls="advanced-features-content"
        id="advanced-features-header"
        sx={{
          width: "fit-content"
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Advanced Features
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ py: 0 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={studioVoiceEnabled}
              onChange={handleStudioVoiceEnabled}
              name="studioVoiceEnabled"
              color="primary"
            />
          }
          label={
            <Typography variant="body1" color="text.secondary">
              Disable Voice Cloning
            </Typography>
          }
        />
      </AccordionDetails>
    </Accordion>
  );
};

export default AdvancedFeatures;
